"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, Loader, Check } from "lucide-react";
import { toast } from "sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
export default function EnthusiastNewsletter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="enthusiast-newsletter" ref={ref} className={`dark bg-background text-foreground py-16 border-y border-border transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 text-center">
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight mb-3">
              Enthusiast Newsletter Signup
            </h2>
            <p className="text-muted-foreground">
              Join the community for monthly chemicals, grain, and stories.
            </p>
          </div>

          <FormProvider formId="newsletter_signup_technical_notes" sectionName="enthusiast-newsletter" defaultValues={{
          first_name: "",
          email: "",
          interests: [],
          consent: false
        }} validationRules={{
          first_name: {
            required: false
          },
          email: {
            required: true,
            pattern: "email"
          },
          interests: {
            required: false
          },
          consent: {
            required: true
          }
        }} submitText="" successMessage="Inquiry Sent Inquiry received. We will be in touch shortly with a quote.">
            <NewsletterForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Inquiry Sent Inquiry received. We will be in touch shortly with a quote.");
      setIsDialogOpen(true);
    }
  }, [isSubmitted, submitError]);
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };
  const interestOptions = ["Black-and-White Film", "Color Film Techniques", "Equipment Reviews", "Behind-the-Scenes Stories"];
  return <>
      <Form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
          <div className="md:col-span-4">
            <FormField control={control} name="first_name" render={({
            field
          }) => <FormItem>
                  <FormLabel className="sr-only">First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane" className="bg-secondary/20 border-border/50 focus:border-primary h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="md:col-span-7">
            <FormField control={control} name="email" render={({
            field
          }) => <FormItem>
                  <FormLabel className="sr-only">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" className="bg-secondary/20 border-border/50 focus:border-primary h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="md:col-span-1 flex justify-center md:justify-start">
            <Button type="submit" disabled={isSubmitting} size="icon" className="h-12 w-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all">
              {isSubmitting ? <Loader className="size-5 animate-spin" /> : <ArrowRight className="size-5" />}
              <span className="sr-only">Subscribe</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2 border-t border-border/30">
          <FormField control={control} name="interests" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-sm font-medium text-muted-foreground mb-3 block">
                  Areas of Interest
                </FormLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {interestOptions.map((option, i) => <div key={option} data-index={i} className="flex items-center space-x-2">
                      <FormControl>
                        <input type="checkbox" className="appearance-none size-4 border border-input rounded-sm bg-transparent checked:bg-primary checked:border-primary focus:ring-1 focus:ring-ring transition-colors cursor-pointer relative" checked={field.value?.includes(option)} onChange={e => {
                  const checked = e.target.checked;
                  const current = field.value || [];
                  if (checked) {
                    field.onChange([...current, option]);
                  } else {
                    field.onChange(current.filter((v: string) => v !== option));
                  }
                }} />
                      </FormControl>
                      <span className="text-sm text-muted-foreground/80">
                        {option}
                      </span>
                    </div>)}
                </div>
                <FormMessage />
              </FormItem>} />

          <div className="flex flex-col justify-end">
            <FormField control={control} name="consent" render={({
            field
          }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border/30 p-4 bg-secondary/10">
                  <FormControl>
                    <input type="checkbox" className="mt-1 appearance-none size-4 border border-input rounded-sm bg-transparent checked:bg-primary checked:border-primary focus:ring-1 focus:ring-ring transition-colors cursor-pointer" checked={field.value} onChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium text-foreground">
                      Subscribe to Newsletter
                    </FormLabel>
                    <FormDescription className="text-xs text-muted-foreground">
                      I agree to receive the FilmFrame enthusiast newsletter.
                    </FormDescription>
                  </div>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm text-center">
            {submitError}
          </div>}
      </Form>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader className="flex flex-col items-center text-center">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Check className="size-6 text-primary" />
            </div>
            <DialogTitle className="text-2xl font-serif">Inquiry Sent</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Inquiry received. We will be in touch shortly with a quote.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button type="button" variant="default" onClick={handleCloseDialog} className="w-full sm:w-auto px-8">
              Back to Technical Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}